import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store, createSelector } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import type Quill from 'quill/core';
import { Delta } from 'quill/core';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, map, startWith, switchMap, take, tap, timeout } from 'rxjs';
import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { EventAttendeeRoles, JobEventStatus, MAX_32_BIT_INT, SM_BREAKPOINT, eventAttendeeRolesArray, eventTypeInfoMapV2 } from 'src/app/global.constants';
import { isFinalizedInvoice } from 'src/app/invoices/invoices.utils';
import { convertCentsToDollars } from 'src/app/lib.ts/currency.util';
import { DetailsHelperService } from 'src/app/services/details-helper.service';
import { FreyaHelperService } from 'src/app/services/freya-helper.service';

import { ResponsiveHelperService } from 'src/app/services/responsive-helper.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { preventKeyboardOnInputNumberButtonPress } from 'src/app/utilities/input.util';
import { getZoneActivePrice } from 'src/app/utilities/product.util';

import { SubSink } from 'subsink';

import { Charge, Discount, FullJobFragment } from '../../../generated/graphql.generated';
import { isEstimateArtifact } from '../../artifacts/artifact.util';
import { ArtifactService } from '../../core/public-api';
import { selectScriptLoaded, selectElements } from '../../quickjs/quickjs.feature';
import { brandingFeature, createConfigSelector, selectEventTypes, selectNavigatorUrlsByEventType } from '../../state/branding.store';
import { selectFeature } from '../../state/featureFlags.store';
import { AddDiscountsModalComponent } from '../add-discounts-modal/add-discounts-modal.component';
import { AddEventButtonComponent } from '../add-event-button/add-event-button.component';
import { calculateJobTotals } from '../job-calculator';

import { deletableEventStatuses, getDisabledStatus } from '../job-charges-helpers';
import { JobEstimateComponent } from '../job-estimate/job-estimate.component';
import { validateLocationsForEventType , getPromoteEventInfo, isNavigatorStarted, resolveNavigatorUrl, formatEventDate } from '../job-helpers';
import { OverviewChipComponent } from '../job-overview/overview-header/overview-chip/overview-chip.component';
import { OverviewSummaryComponent } from '../job-overview/overview-summary/overview-summary.component';
import { JobSaveGateService } from '../job-save-gate.service';
import {
  jobToolFeature,
  selectJob,
  selectJobInCurrentZone,
  selectLatestEstimate,
  selectOlderEstimates,
  selectCalculatorUrl,
  selectJobLoading,
  selectJobUpdating,
} from '../job-tool.reducer';




import { AddChargesComponent } from './add-charges/add-charges.component';
import { EventsTableComponent } from './events-table/events-table.component';
import { JobFinancialsTableComponent } from './financials-table/financials-table.component';
import { WorkOrdersActions } from './job-workorders.actions';
import { jobWorkordersFeature } from './job-workorders.feature';
import { WorkorderEventAttachment } from './job-workorders.interfaces';
import {
  selectAddEstimateBlockedReason,
  selectCanModifyLockedEvents,
  selectCanRunNavigator,
  selectEventUploadsInFlight,
  selectLockDate,
  workOrdersSelectors,
} from './job-workorders.selectors';
import { EditableWorkorder } from './job-workorders.state';
import { MrCalculatorComponent } from './mr-calculator/mr-calculator.component';
import { NavigatorStatusComponent } from './navigator-status/navigator-status.component';
import { UpsellProductsDialogComponent } from './upsell-products-dialog/upsell-products-dialog.component';

export const selectJobTotals = createSelector(
  selectJob,
  jobWorkordersFeature.selectAvailableDiscounts,
  jobWorkordersFeature.selectWorkordersEditor,
  brandingFeature.selectProducts,
  brandingFeature.selectBranding,
  (job, availableDiscounts, { workorders }, availableProducts, { zoneId }) => {

    if (!job) { return; }

    const jobCopy = cloneDeep(job);

    const latestEvents: typeof jobCopy['events'] = [];

    for (const workorder of workorders) {

      if (workorder.removed) { continue; }

      const event: typeof latestEvents[number] = jobCopy.events?.find((e) => e.id === workorder.eventId) || {
        id: workorder.eventId,
        title: workorder.title,
        type: workorder.type,
        status: 'required',
        discounts: [],
        charges: [],
        invoices: [],
        createdAt: 0,
        updatedAt: 0,
      };

      const latestCharges: typeof event['charges'] = [];

      for (const editableCharge of workorder.charges) {

        if (editableCharge.removed) { continue; }

        const existingCharge = event?.charges?.find((c) => c.id === editableCharge.chargeId);

        const selectedProduct = availableProducts?.find((p) => p.id === editableCharge.productId);

        const price = existingCharge?.price || getZoneActivePrice(selectedProduct?.prices || [], zoneId);

        const chargeAttributes = existingCharge
          ? existingCharge.attributes || []
          : getZoneActivePrice(selectedProduct?.prices || [], zoneId)?.attributes || [];

        latestCharges.push({
          id: editableCharge.chargeId,
          quantity: editableCharge.quantity,
          amount: price?.priceType === 'percentage'
            ? editableCharge.amount
            : Math.round(editableCharge.amount * 100),
          currency: job.currency,
          //if charge is added from product and later product prices are deactivated, it will not appear in availableProducts
          //fallback to product stored in event.charges
          product: selectedProduct || existingCharge?.product,
          price,
          taxes: editableCharge.taxes.map((t) => ({ ...t, name: 'Tax', active: true })),
          attributes: chargeAttributes,
        });
      }

      event.charges = latestCharges;

      const latestDiscounts: typeof event['discounts'] = [];

      for (const discount of workorder.discounts) {

        if (discount.removed) { continue; }

        const existingDiscount = event.discounts?.find((d) => d.appliedId === discount.appliedId);

        const selectedDiscount = existingDiscount?.discount || availableDiscounts?.find((d) => d.id === discount.discountId);

        latestDiscounts.push({
            appliedId: discount.appliedId,
            appliedCustomAmount: discount.customAmount,
            discount: selectedDiscount || {
              name: '',
              code: '',
              id: '',
              amount: discount.customAmount,
              active: true,
              updatedAt: 1,
              createdAt: 1,
              discountType: discount.discountType,
            },
            appliedAt: 0,
            eventId: workorder.eventId,
        });
      }

      event.discounts = latestDiscounts;

      latestEvents.push(event);
    }

    jobCopy.events = latestEvents;

    const jobWithRecalculatedTotals = calculateJobTotals(jobCopy);

    return jobWithRecalculatedTotals;
  }
);

function getCrewRolePriority(role: string): number {
  switch (role) {
    case EventAttendeeRoles.crewLead: return 1;
    case EventAttendeeRoles.assistantCrewLead:
    case EventAttendeeRoles.driver:
    case EventAttendeeRoles.crewMember: return 2;
    default: return 99;
  }
}

function filterAndSortCrew(attendees: any[] | null | undefined): any[] {
  if (!attendees?.length) return [];
  const uniqueMap = new Map<string, any>();
  for (const a of attendees) {
    if (!eventAttendeeRolesArray.includes(a.role)) continue;
    const existing = uniqueMap.get(a.user?.id);
    if (!existing || getCrewRolePriority(a.role) < getCrewRolePriority(existing.role)) {
      uniqueMap.set(a.user?.id, a);
    }
  }
  return Array.from(uniqueMap.values()).sort((a, b) => {
    const pd = getCrewRolePriority(a.role) - getCrewRolePriority(b.role);
    if (pd !== 0) return pd;
    const nameA = [a.user?.familyName, a.user?.givenName].filter(Boolean).join(', ');
    const nameB = [b.user?.familyName, b.user?.givenName].filter(Boolean).join(', ');
    return nameA.localeCompare(nameB);
  });
}

export const selectWorkorders = createSelector(
    jobWorkordersFeature.selectWorkordersEditor,
    selectJobTotals,
    selectCanModifyLockedEvents,
    selectLockDate,
    createConfigSelector('NAVIGATOR_URL'),
    selectNavigatorUrlsByEventType,
    selectEventTypes,
    (
      { workorders },
      job,
      canModifyLockedEvents,
      lockDate,
      configDefaultNavigatorUrl,
      navigatorUrlsByEventType,
      eventTypes,
    ) => {

      return workorders
        .filter((wo) => !wo.removed && !wo.cancelled)
        .map((wo) => {

          const event = job?.events?.find((e) => e.id === wo.eventId);

          const isLockDatePassed = event?.end && (lockDate > event?.end);

          const locked = Boolean(isLockDatePassed && !canModifyLockedEvents);

          const invoiced = Boolean(event?.invoices?.some(isFinalizedInvoice));
          const hasActiveInvoice = Boolean(event?.invoices?.some((invoice) => !invoice.deletedAt && !invoice.voidedAt));

          const { disabledStatus, disabledToolTip, disabledWarning } = getDisabledStatus({ locked, invoiced, type: event?.type }, job);

          const {
            disabledStatus: bookButtonDisabled,
            disabledToolTip: bookButtonDisabledTooltip,
          } = getDisabledStatus({
            locked,
            invoiced,
            type: event?.type,
            requireSignedEstimate: eventTypes?.[event?.type]?.requireSignedEstimate,
          }, job, true);

          const charges = wo.charges
            .filter((c) => !c.removed)
            .map((editableCharge) => {

              const charge = event?.charges?.find((c) => c.id === editableCharge.chargeId);

              return {
                ...editableCharge,
                chargeSubTotal: charge.chargeSubTotal,
                borderColor: charge.product?.metadata?.color,
                productName: charge.product?.name || charge?.productName || editableCharge.productName,
                category: charge.product?.category || 'None',
                priceType: charge.price?.priceType || 'fixed',
              };
            });

          const discounts = wo.discounts
            .filter((d) => !d.removed)
            .map((editableDiscount) => {

              const associatedDiscount = event?.discounts?.find((d) => d.appliedId === editableDiscount.appliedId);

              const discountType = associatedDiscount?.discount?.discountType || editableDiscount.discountType;

              return {
                ...editableDiscount,
                amount: editableDiscount.customAmount || associatedDiscount.discount?.amount,
                discountedAmount: associatedDiscount.discountedAmount,
                discountType,
                associatedDiscount,
              };
            });

          const hasChargesUnsavedChanges = wo.charges.some((c) => c.new || c.modified || (!c.new && c.removed));

          const hasUnsavedChanges = wo.new || wo.modified || (!wo.new && wo.removed) || hasChargesUnsavedChanges;

          // Event starts within 8 hours from now (or has already started)
          const nowSec = Date.now() / 1000;
          const eightHoursFromNow = nowSec + 8 * 60 * 60;
          const isUpcoming = !!event.start && event.start <= eightHoursFromNow;

          // Determine if the navigator has actually been opened (has timing data)
          // vs just having navigatorCurrentStep='started' from a prior reset or first write
          const navigatorActuallyStarted = isNavigatorStarted(event);

          // Shares the resolution chain with `selectNavigatorUrlForEvent`, so an event only
          // shows a navigator button when launching it would find the same script.
          const resolvedNavigatorUrl = resolveNavigatorUrl(event, {
            jobNavigatorUrl: job?.metadata?.navigatorUrl,
            typeUrls: navigatorUrlsByEventType,
            eventTypes,
            defaultUrl: configDefaultNavigatorUrl,
          });

          return {
            ...wo,
            title: event.title || wo.title,
            assets: event.assets,
            crew: filterAndSortCrew(event.attendees),
            rawAttendees: event.attendees || [],
            start: event.start,
            eventDate: formatEventDate(event.start, job.zone?.timezone),
            status: event.status,
            displayStatus: hasActiveInvoice ? 'Invoiced' : event.status,
            // Per-event rich notes (Ray: archive of what was done per event).
            // Serialized Quill delta JSON string; '' when unset.
            // Read from the editable workorder state so unsaved edits are reflected.
            eventNotes: wo.eventNotes,
            discountedSubTotal: event.discountedSubTotal,
            locked,
            invoiced,
            charges,
            discounts,
            hasUnsavedChanges,
            bookButtonDisabled,
            bookButtonDisabledTooltip,
            disabledStatus,
            disabledToolTip,
            disabledWarning,
            isUpcoming,
            navigatorCurrentStep: event.navigatorCurrentStep ?? null,
            navigatorData: event.navigatorData ?? null,
            navigatorUrl: resolvedNavigatorUrl,
            navigatorActuallyStarted,
          }
        });
      }
);

export const selectCancelledEvents = createSelector(
  selectJob,
  jobWorkordersFeature.selectWorkordersEditor,
  (job, { workorders }) => {
    if (!job) { return []; }

    return [...job.events]
      .filter((event) => {

        if (event.status === 'cancelled') return true;

        const workorder = workorders?.find((wo) => wo.eventId === event.id);

        if (!workorder) return false;

        return workorder.cancelled;
      })
      .sort((a, b) => a.sequentialOrder - b.sequentialOrder)
      .map((event) => {
        const charges = [...(event.charges || [])]
          .sort((a, b) => a.order - b.order)
          .map((charge) => {
            const chargeAmount = charge.amount ?? charge.price?.amount ?? 0;
            const priceType = charge.price?.priceType || 'fixed';
            const amount = priceType === 'fixed' ? chargeAmount / 100 : chargeAmount;

            return {
              chargeId: charge.id,
              productId: charge.product?.id,
              productName: charge.product?.name || charge.productName,
              category: charge.product?.category || 'None',
              borderColor: charge.product?.metadata?.color,
              amount,
              quantity: charge.quantity,
              priceType,
              chargeSubTotal: charge.chargeSubTotal,
            };
          });

        const discounts = (event.discounts || []).map((d) => ({
          appliedId: d.appliedId,
          name: d.discount?.name,
          code: d.discount?.code,
          amount: d.appliedCustomAmount || d.discount?.amount,
          discountType: d.discount?.discountType,
          discountedAmount: d.discountedAmount,
          associatedDiscount: d,
        }));

        return {
          eventId: event.id,
          title: event.title,
          type: event.type,
          status: event.status,
          start: event.start,
          eventDate: formatEventDate(event.start, job.zone?.timezone),
          assets: event.assets,
          crew: filterAndSortCrew(event.attendees),
          discountedSubTotal: event.discountedSubTotal,
          charges,
          discounts,
        };
      });
  }
);

@Component({
  selector: 'app-job-workorders',
  standalone: true,
  imports: [
    SharedModule,
    FreyaCommonModule,
    AddChargesComponent,
    AddDiscountsModalComponent,
    AddEventButtonComponent,
    EventsTableComponent,
    JobFinancialsTableComponent,
    MrCalculatorComponent,
    JobEstimateComponent,
    OverviewChipComponent,
    OverviewSummaryComponent,
    NavigatorStatusComponent,
    QuillEditorComponent,
  ],
  templateUrl: './job-workorders.component.html',
  styleUrls: [
    './job-workorders.component.scss',
    '../job-financials/document-container-style.scss',
    '../chips-container.scss',
  ],
})
export class JobWorkordersComponent implements OnDestroy, AfterViewInit {

  @ViewChildren('workorderRef') workorderQueryList!: QueryList<ElementRef<HTMLElement>>;

  constructor(
    public store: Store,
    private dialogService: DialogService,
    public freyaHelper: FreyaHelperService,
    public responsiveHelper: ResponsiveHelperService,
    private detailsHelper: DetailsHelperService,
    private elementRef: ElementRef,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private artifactService: ArtifactService,
    private saveGate: JobSaveGateService,
    private messageService: MessageService,
  ) {}

  // ---- Event Notes (per-event, mover-facing) -----------------------------
  // Ray: an archive of what was done per event — collapsible, rich text,
  // included in crew comms. Stored on event metadata.eventNotes as a
  // serialized Quill delta (same convention as the dispatch dayNotes).
  public notesExpanded: Record<string, boolean> = {};

  public notesDeltas = new Map<string, Delta>();

  public eventNotesModules: QuillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
    ],
    // Ray: "Add Attachment" inside the Event Notes tools — files/pictures
    // assigned directly to THE event (metadata.eventAttachments), reusing
    // the client account's Activity attachment wheel (#6983).
    attachmentToolbar: true,
  };

  public toggleNotes(eventId: string): void {
    this.notesExpanded[eventId] = !this.isNotesExpanded(eventId);
    this.persistNotesExpansion(eventId);
  }

  public isNotesExpanded(eventId: string): boolean {
    this.loadNotesExpansion();
    return this.notesExpanded[eventId] ?? true;
  }

  // ---- Event Notes collapse persistence (Ray: once collapsed, stays
  // collapsed until someone manually reopens it — survives reloads) ----
  private static readonly NOTES_EXPANDED_KEY = 'workorders.eventNotesExpanded';
  private notesExpandedLoaded = false;

  private loadNotesExpansion(): void {
    if (this.notesExpandedLoaded) return;
    this.notesExpandedLoaded = true;
    try {
      const raw = localStorage.getItem(JobWorkordersComponent.NOTES_EXPANDED_KEY);
      if (raw) this.notesExpanded = JSON.parse(raw) ?? {};
    } catch {
      // Corrupt state — keep the (expanded) default.
    }
  }

  private persistNotesExpansion(eventId: string): void {
    try {
      // Re-read + merge instead of writing the cached map wholesale:
      // with several instances open (a second tab or view) a full-map write
      // would roll back another event's saved state.
      const raw = localStorage.getItem(JobWorkordersComponent.NOTES_EXPANDED_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const latest: Record<string, boolean> =
          parsed && typeof parsed === 'object' && !Array.isArray(parsed)
              ? { ...parsed }
              : {};
      latest[eventId] = this.notesExpanded[eventId];
      this.notesExpanded = { ...this.notesExpanded, ...latest };
      localStorage.setItem(JobWorkordersComponent.NOTES_EXPANDED_KEY, JSON.stringify(latest));
    } catch {
      // Storage full/unavailable — collapse state stays session-only.
    }
  }

  public getNotesDelta(eventId: string, serialized: string): Delta {
    // Memoize so ngModel doesn't receive a new reference each CD cycle
    if (this.notesDeltas.has(eventId)) {
      return this.notesDeltas.get(eventId)!;
    }
    if (serialized) {
      try {
        const parsed = JSON.parse(serialized);
        if (parsed?.ops) {
          const delta = new Delta(parsed.ops);
          this.notesDeltas.set(eventId, delta);
          return delta;
        }
      } catch {
        // Legacy plain-text content — wrap it so the words survive instead
        // of discarding the note.
        const legacy = new Delta().insert(serialized);
        this.notesDeltas.set(eventId, legacy);
        return legacy;
      }
    }
    const empty = new Delta();
    this.notesDeltas.set(eventId, empty);
    return empty;
  }

  public onNotesChanged(eventId: string, delta: Delta): void {
    const serialized = JSON.stringify({ ops: delta.ops });
    // Show the typed content immediately (local memo)
    this.notesDeltas.set(eventId, delta);
    this.store.dispatch(this.actions.notesChanged({ eventId, eventNotes: serialized }));
  }

  // ---- Event Notes 📎 (Ray: assign a document or picture directly to a
  // specific event, not only the BOL — #6983). Same wheel as the Dispatch
  // page's Event Notes: artifacts + event metadata.eventAttachments, saved
  // with the workorders save (metadata merged key-level, so eventNotes and
  // eventAttachments never clobber each other).
  public onNotesEditorCreated(editor: Quill, event: EditableWorkorder): void {
    const attachmentModule = editor.getModule('attachmentToolbar') as
      | {
          onFilesSelected?: (files: File[]) => void;
          onFilesRejected?: (rejected: { file: File; reason: string }[]) => void;
        }
      | undefined;
    if (!attachmentModule) return;

    attachmentModule.onFilesRejected = (rejected) => {
      for (const { file, reason } of rejected) {
        this.messageService.add({
          severity: 'error',
          summary: 'File not allowed',
          detail: reason === 'size'
            ? `"${file.name}" exceeds the size limit`
            : `"${file.name}" is not a supported file type`,
        });
      }
    };

    attachmentModule.onFilesSelected = (files) => {
      void this.onEventNotesFilesSelected(event.eventId, files);
    };
  }

  private async onEventNotesFilesSelected(eventId: string, files: File[]): Promise<void> {
    // Publish BEFORE any network work: a save starting while the upload is in
    // flight must wait, or it snapshots the attachment list without the file
    // and the save-success reload drops it (CodeRabbit).
    // Conversely an upload starting mid-save would be missing from the
    // save's snapshot and lost to the success reload — acquire() atomically
    // waits for the page's other writers and claims the upload slot.
    await firstValueFrom(this.saveGate.acquire('upload'));
    this.store.dispatch(this.actions.eventUploadStarted({ eventId }));
    try {
        await this.uploadEventNotesFiles(eventId, files);
    } finally {
        this.saveGate.endUpload();
        this.store.dispatch(this.actions.eventUploadFinished({ eventId }));
    }
  }

  /** Marcelo (MR !2424 review): a hung upload used to hold the save gate
   *  open forever (every Save on the page waits until reload). One generous
   *  cap per request keeps legit slow-link uploads alive; a stuck request
   *  errors, runs the artifact cleanup and releases the gate with a toast. */
  private static readonly UPLOAD_TIMEOUT_MS = 3 * 60 * 1000;

  private async uploadEventNotesFiles(eventId: string, files: File[]): Promise<void> {
    for (const file of files) {
      try {
        const result = await firstValueFrom(
          this.artifactService.createArtifact([{
            contentType: file.type,
            fileSize: file.size,
            name: file.name,
            namespace: 'event-attachment',
            public: true,
          }]).pipe(timeout(JobWorkordersComponent.UPLOAD_TIMEOUT_MS)),
        );
        const artifact = result.data.createArtifacts.artifacts[0];
        if (!artifact) throw new Error('No artifact returned from server');
        const policy = JSON.parse(artifact.signedPolicy);
        try {
          await firstValueFrom(
            this.artifactService.uploadFile(file as any, policy)
              .pipe(timeout(JobWorkordersComponent.UPLOAD_TIMEOUT_MS)),
          );
        } catch (uploadError) {
          // createArtifact succeeded but the upload did not: the record would
          // linger with no attached file (CodeRabbit). Clean it up under the
          // same cap — a hung cleanup request would otherwise hold the outer
          // try's cleanup (and the save gate) open until the network stack
          // gives up (CR: bound the cleanup request). A timeout/error here is
          // logged and the original upload error still propagates.
          try {
            await firstValueFrom(
              this.artifactService.deleteArtifacts([artifact.id])
                .pipe(timeout(JobWorkordersComponent.UPLOAD_TIMEOUT_MS)),
            );
          } catch (cleanupError) {
            console.error('Artifact cleanup failed after failed upload', artifact.id, cleanupError);
          }
          throw uploadError;
        }

        // Re-read the state base right before each dispatch is no longer
        // needed: the dispatch is a SMALL event-level action and the reducer
        // appends to the CURRENT in-reducer list — concurrent uploads (or a
        // concurrent remove) cannot drop each other (CodeRabbit).
        this.store.dispatch(this.actions.eventAttachmentUploaded({
          eventId,
          attachment: {
            artifactId: artifact.id,
            name: artifact.name || file.name,
            contentType: artifact.contentType || file.type,
            fileSize: artifact.fileSize ?? file.size,
            url: artifact.url,
          },
        }));
      } catch {
        this.messageService.add({
          severity: 'error',
          summary: 'Upload failed',
          detail: `Could not upload "${file.name}"`,
        });
      }
    }
  }

  public getEventNotesAttachments(event: { eventId: string; eventAttachments?: WorkorderEventAttachment[] }): WorkorderEventAttachment[] {
    return event.eventAttachments ?? [];
  }

  public async onRemoveEventNotesAttachment(event: { eventId: string }, attachment: WorkorderEventAttachment): Promise<void> {
    // Serialize with the page's other writers before reading and dispatching
    // (CodeRabbit): acquire('upload') waits for any in-flight save/ops write/
    // upload to clear, and — the normal-save slot now being held across the
    // save's whole snapshot+mutation section in job.effects — the save can
    // no longer carry the pre-removal list, nor can a save that already left
    // restore the attachment after its success rebuild: the removal lands
    // after it, on the rebuilt state.
    await firstValueFrom(this.saveGate.acquire('upload'));
    try {
      // Atomic action: the reducer filters the CURRENT in-reducer list — no
      // whole-array read-modify-write that an overlapping upload could
      // replace (CodeRabbit).
      this.store.dispatch(this.actions.removeAttachmentButtonClicked({
        eventId: event.eventId,
        artifactId: attachment.artifactId,
      }));
    } finally {
      this.saveGate.endUpload();
    }
  }

  public openEventNotesAttachment(attachment: WorkorderEventAttachment): void {
    // Belt-and-braces with the reducer's parse: only http(s) URLs may be
    // opened from a chip — the url comes from event metadata anyone with
    // event-write can author (CodeRabbit round 17).
    if (attachment.url && /^https?:\/\//i.test(attachment.url)) {
      window.open(attachment.url, '_blank', 'noopener');
    }
  }

  public eventTypeInfoMap = eventTypeInfoMapV2;

  public actions = WorkOrdersActions;

  resizeObserver: ResizeObserver;

  intersectionObserver: IntersectionObserver

  subs = new SubSink();

  width: number | undefined;

  smBreakpoint = SM_BREAKPOINT;

  subtotalLimit = MAX_32_BIT_INT;

  showMoreEstimates = false;

  private _selectChargeActions = createSelector(
    jobWorkordersFeature.selectWorkordersEditor,
    selectWorkorders,
    ({ actionsMenuChargeId }, workorders) => {
      if (!actionsMenuChargeId) {
        return [];
      }

      // Find the event that contains this charge
      const eventWithCharge = workorders?.find(workorder => 
        workorder.charges?.some(charge => charge.chargeId === actionsMenuChargeId)
      );

      if (!eventWithCharge) {
        return [];
      }

      return [
        {
          label: 'Remove',
          icon: 'pi pi-trash',
          command: () => this.store.dispatch(this.actions.removeChargeMenuItemClicked({ chargeId: actionsMenuChargeId })),
        },
        {
          label: 'Edit',
          icon: 'pi pi-pencil',
          command: () => this.store.dispatch(this.actions.editChargeButtonClicked(
            {
              chargeId: actionsMenuChargeId,
              eventId: eventWithCharge?.eventId,
            })),
        }
      ];
    }
  );

  private _selectEventActions = createSelector(
    selectJob,
    selectJobLoading,
    jobWorkordersFeature.selectWorkordersEditor,
    selectWorkorders,
    (job, jobLoading, { actionsMenuEventId  }, workorders ) => {

      if (!actionsMenuEventId) {
        return [];
      }

      const workorderIndex = workorders.findIndex((e) => e.eventId === actionsMenuEventId);

      if (workorderIndex < 0) {
        return [];
      }

      const event = workorders[workorderIndex];

      const disabled = jobLoading || event.locked || event.invoiced;

      const promoteEventInfo = getPromoteEventInfo(event);

      return [
        {
          label: 'Move To Top',
          icon: 'pi pi-arrow-circle-up',
          visible: workorderIndex > 0,
          command: () => this.store.dispatch(this.actions.moveToTopMenuItemClicked({ eventId: actionsMenuEventId })),
        },
        {
          label: 'Move Up',
          icon: 'pi pi-arrow-up',
          visible: workorderIndex > 0,
          command: () => this.store.dispatch(this.actions.moveUpMenuItemClicked({ eventId: actionsMenuEventId })),
        },
        {
          label: 'Move Down',
          icon: 'pi pi-arrow-down',
          visible: workorderIndex < workorders.length - 1,
          command: () => this.store.dispatch(this.actions.moveDownMenuItemClicked({ eventId: actionsMenuEventId })),
        },
        {
          label: 'Move To Bottom',
          icon: 'pi pi-arrow-circle-down',
          visible: workorderIndex < workorders.length - 1,
          command: () => this.store.dispatch(this.actions.moveToBottomMenuItemClicked({ eventId: actionsMenuEventId })),
        },
        {
          label: [ 'booked', 'confirmed', 'completed' ].includes(event.status) ? 'Reschedule' : 'Book',
          icon: 'pi pi-calendar-plus',
          command: () => this.store.dispatch(this.actions.scheduleEventMenuItemClicked()),
          visible: !event.hasUnsavedChanges,
        },
        {
          label: 'Save',
          icon: 'pi pi-save',
          command: () => this.store.dispatch(this.actions.saveMenuItemClicked()),
          visible: event.hasUnsavedChanges,
        },
        {
          label: promoteEventInfo.label,
          icon: promoteEventInfo.icon,
          command: () => this.store.dispatch(this.actions.promoteEventMenuItemClicked()),
          visible: promoteEventInfo.visible,
        },
        {
          label: 'Duplicate',
          icon: 'pi pi-clone',
          command: () => this.store.dispatch(this.actions.duplicateMenuItemClicked()),
        },
        {
          id: 'cancel',
          label: 'Cancel',
          icon: 'pi pi-ban',
          command: () => this.store.dispatch(this.actions.cancelMenuItemClicked({ eventId: actionsMenuEventId })),
          disabled,
          visible: !deletableEventStatuses.includes(event.status as JobEventStatus),
        },
        {
          id: 'delete',
          label: 'Delete',
          icon: 'pi pi-trash',
          command: () => this.store.dispatch(this.actions.deleteMenuItemClicked({ eventId: actionsMenuEventId })),
          disabled: disabled || Boolean(job.closedAt),
          visible: deletableEventStatuses.includes(event.status as JobEventStatus),
        },
        {
          label: 'View History',
          icon: 'pi pi-book',
          command: () => this.store.dispatch(this.actions.viewHistoryMenuItemClicked()),
          visible: !event.new,
        },
        //TO DO - rework ShareEventZoneComponent to make it compatible with global state
        //for now we use quick solution, as events are not shared often and even if it's shared
        //and state is not aware it doesn't affect following work in workorders
        {
          id: 'share',
          label: 'Share',
          icon: 'pi pi-arrow-up-right',
          command: () => this.store.dispatch(this.actions.shareMenuItemClicked()),
        },
        {
          id: 'update-navigator',
          label: 'Update Navigator',
          icon: 'pi pi-map',
          visible: Boolean(job?.events?.find(e => e.id === actionsMenuEventId)?.navigatorCurrentStep),
          command: () => this.store.dispatch(this.actions.updateNavigatorMenuItemClicked({ eventId: actionsMenuEventId })),
        },
      ];
    }
  );

  private _selectDiscountActions = createSelector(
    jobWorkordersFeature.selectWorkordersEditor,
    jobWorkordersFeature.selectAvailableDiscounts,
    selectJob,
    ({ actionsMenuAppliedDiscountId, workorders }, availableDiscounts, job ) => {

      if (!actionsMenuAppliedDiscountId) { return [] };

      const editableDiscounts = workorders?.flatMap((wo) => wo.discounts || []) || [];

      const editableDiscount = editableDiscounts?.find((ad) => ad.appliedId === actionsMenuAppliedDiscountId);

      const jobDiscounts = job?.events
        ?.flatMap((e) => e.discounts || [])
        .map((d) => d?.discount) || [];

      const discount = [ ...jobDiscounts, ...(availableDiscounts || []) ].find((d) => d.id === editableDiscount?.discountId);

      const hasCustomRange = discount?.customAmountRange?.length > 0;

      return [
        {
          label: 'Edit',
          icon: 'pi pi-pencil',
          command: () => {},
            //disabled: !hasCustomRange,
            //TO DO - implement this logic
            disabled: true,
          tooltipOptions: {
            tooltipLabel: !hasCustomRange && 'Discount wasn\'t given a custom amount range',
            tooltipPosition: 'left',
          }
        },
        {
          label: 'Remove',
          icon: 'pi pi-trash',
          command: () => {
            const eventWithDiscount = workorders?.find(workorder => 
              workorder.discounts?.some(discount => discount.appliedId === actionsMenuAppliedDiscountId)
            );
            if (eventWithDiscount && actionsMenuAppliedDiscountId) {
              this.store.dispatch(this.actions.removeDiscountMenuItemClicked({ 
                eventId: eventWithDiscount.eventId, 
                appliedId: actionsMenuAppliedDiscountId 
              }));
            }
          },
        }

      ];
    }
  );

  public viewModel$ = this.store.select(createSelector({
    workorders: selectWorkorders,
    cancelledEvents: selectCancelledEvents,
    eventActions: this._selectEventActions,
    chargeActions: this._selectChargeActions,
    discountActions: this._selectDiscountActions,
    validEventIds: createSelector(workOrdersSelectors.selectEventIds, ({ validEventIds }) => validEventIds),
    collapsed: createSelector(jobWorkordersFeature.selectWorkordersEditor, ({ collapsed }) => collapsed),
    isAllEventsCollapsed: createSelector(
      selectWorkorders,
      jobWorkordersFeature.selectWorkordersEditor,
      (eventsWithCharges, { collapsed }) => eventsWithCharges.every((wo) => collapsed[wo.eventId]),
    ),
    job: selectJob,
    jobLoading: selectJobLoading,
    jobBeingUpdated: selectJobUpdating,
    // Marcelo (MR !2424 review): Save waits while an Event Notes 📎 upload is
    // in flight — show it on the button instead of a save that "does nothing".
    saveWaitingOnUpload: createSelector(
      selectEventUploadsInFlight,
      (uploads) => uploads > 0,
    ),
    jobLoadingOrUpdating: createSelector(
      selectJobLoading,
      selectJobUpdating,
      (loading, updating) => loading || updating,
    ),
    aiGenerateChargesEnabled: createSelector(
      selectFeature('ai-generate-charges'),
      createConfigSelector('PRICINGENGINE_ENABLED'),
      (feature, configEnabled) => feature && configEnabled,
    ),
    // Hides the crew nav row while the navigator is unreleased. This is visibility only —
    // `jobs/:jobId/navigator/:eventId` stays reachable by URL. Enforcement is server-side:
    // `mutationUpdateEventNavigator` guards on `calendarEvents.edit`, so a user without it
    // can open the page but cannot write to it.
    crewNavigatorEnabled: selectFeature('crew-navigator'),
    canRunNavigator: selectCanRunNavigator,
    defaultDiscount: createSelector(
      jobWorkordersFeature.selectAvailableDiscounts,
      (availableDiscounts) => {
        const [ firstAvailableDiscount ] = availableDiscounts;
        if (!firstAvailableDiscount) {
          return { id: undefined as string | undefined, amount: undefined as number | undefined };
        }
        const amount = firstAvailableDiscount.discountType === 'percentage'
          ? firstAvailableDiscount.amount
          : convertCentsToDollars(firstAvailableDiscount.amount, true);
        return { id: firstAvailableDiscount.id, amount };
      },
    ),
    generateChargesWithAILoading: jobToolFeature.selectAiChargesLoading,
    latestEstimate: selectLatestEstimate,
    olderEstimates: selectOlderEstimates,
    estimatesEmpty: createSelector(
      selectJobLoading,
      selectJob,
      (jobLoading, job) => !jobLoading && job?.artifacts.filter((doc) => isEstimateArtifact(doc)).length === 0,
    ),
    showCalculator: createSelector(
      selectCalculatorUrl,
      selectElements,
      selectScriptLoaded,
      (calculatorUrl, elements, loaded) => Boolean(
        calculatorUrl
        && (!loaded || elements?.length)
      )
    ),
    hasInvalidNewEvents: createSelector(
      selectJob,
      jobWorkordersFeature.selectWorkordersEditor,
      (job, { workorders }) => workorders
        .filter((wo) => wo.new && !wo.removed)
        .some((wo) => !validateLocationsForEventType(job?.locations || [], wo.type).isValid)
    ),
    jobNotInZone: createSelector(selectJobInCurrentZone, (inZone) => !inZone),
    addEstimateBlockedReason: selectAddEstimateBlockedReason,
  }));

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
    this.subs.unsubscribe();

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  ngAfterViewInit(): void {
    this.watchResize();
    this.scrollToWorkorder();
  }

  trackByEventId(index: number, workorder: any): any {
    return workorder?.eventId ?? index;
  }
  trackByChargeId(index: number, charge: any): any {
    return charge?.id ?? index;
  }

  trackByAppliedDiscountId(index: number, discount: any) {
    return discount?.appliedId ?? index;
  }

  handleInputClick(event: any) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      const input = (target.closest('.p-inputnumber') as HTMLElement)?.querySelector('input') as HTMLInputElement;
      input?.blur();
    } else if (typeof event?.target?.select === 'function') {
      event.target.select();
    }
  }

  handleInputTouchStart(event: TouchEvent) {
    preventKeyboardOnInputNumberButtonPress(event);
  }


  //right side panel
  openCharge(job: FullJobFragment, charge?: Charge) {
    if (!charge) {
      return;
    }

    // We are passing the entire job; the alternative would be fetching the charge again from the backend, which is more expensive.
    this.detailsHelper.open('charge', {
        ...charge,
        job,
    });
  }

  openDiscount(discount: Discount) {
    if (!discount) {
      return;
    }

    this.detailsHelper.open('discount', discount);
  }

  //charges updates



  openUpsellProductsDialog(){
    this.dialogService.open(UpsellProductsDialogComponent, {
      header: 'Upsell Products',});
  }

  watchResize() {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.width = entry.contentRect.width;
      }
    });

    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  scrollToWorkorder() {

    const selectedWorkorder$ = this.route.queryParamMap.pipe(
      map(params => params.get('workorderId')),
      distinctUntilChanged()
    );

    const renderedWorkorders$ = this.workorderQueryList.changes.pipe(
      startWith(this.workorderQueryList)
    );

    this.subs.sink = combineLatest([ selectedWorkorder$, renderedWorkorders$ ]).pipe(
      map(([ workorderId, workorders ]) => workorders?.find((ref) => ref.nativeElement.dataset['id'] === workorderId)),
      filter(Boolean),
      switchMap((workorderRef: ElementRef<HTMLElement>) => this.ngZone.onStable.pipe(
        take(1),
        tap(() => {

          workorderRef.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          if (!('IntersectionObserver' in window)) {
            this.highlight(workorderRef, 500);
          } else {
            this.intersectionObserver = new IntersectionObserver(
              ([ entry ], obs) => {
                if (entry.isIntersecting) {
                  this.highlight(workorderRef, 500);
                  obs.disconnect();
                }
              },
              {
                root: null,
                threshold: 0.6,
              },
            );

            this.intersectionObserver.observe(workorderRef.nativeElement);
          }
        }),
      )),
    ).subscribe();
  }

  highlight(el: ElementRef, delay: number) {
    el.nativeElement.style['transition'] = 'box-shadow 0.2s, border-color 0.2s';
    el.nativeElement.style['box-shadow'] = '0 0 0 0.2rem var(--primary-50)';
    el.nativeElement.style['border-color'] = 'var(--primary-50)';

    setTimeout(() => {
      el.nativeElement.style['box-shadow'] = '';
      el.nativeElement.style['border-color'] = '';
    }, delay);
  }

  trackByKey(index: number, formElement: any): string {
    return formElement.key;
  }
}
